import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaEdit, FaPlus, FaSyncAlt, FaTimes, FaTrash } from 'react-icons/fa';
import CollapsibleFilters from './CollapsibleFilters';
import { useNavigate } from 'react-router-dom';
import OrderStatusFilterDropdown from './OrderStatusFilterDropdown';
import OrdersSortableTh from './OrdersSortableTh';
import OrdersTablePanel from './OrdersTablePanel';
import OrdersTablePagination from './OrdersTablePagination';
import OrderStatusPill from './ui/OrderStatusPill';
import OrderItemsSummary from './ui/OrderItemsSummary';
import CateringViewSwitch from './CateringViewSwitch';
import {
    fetchCateringQuotesByRestaurant,
    isTerminalQuoteStatus,
    normalizeCateringQuote,
    updateCateringQuote,
} from './cateringQuoteApi';
import { fetchCateringMenuItemsForTemplate } from './cateringMenuApi';
import { buildSortHandlers } from './ordersTableUtils';
import { formatMobileDisplayOrFallback } from './formatDisplay';
import { confirmAction } from './ui/feedback';
import './css/TodayOrder.css';
import './css/Menu.css';
import './css/cateringViewSwitch.css';
import './css/OrderDetails.css';
import './css/cateringQuoteCreate.css';

export const CATERING_QUOTE_STATUS_OPTS = [
    { code: 'REQUESTED', label: 'Requested', matchNorm: ['REQUESTED'] },
    { code: 'UNDER_REVIEW', label: 'Under Review', matchNorm: ['UNDERREVIEW'] },
    { code: 'CONFIRMED', label: 'Confirmed', matchNorm: ['CONFIRMED'] },
    { code: 'REJECTED', label: 'Rejected', matchNorm: ['REJECTED'] },
];

const QUOTE_STATUS_EDIT_OPTIONS = CATERING_QUOTE_STATUS_OPTS.map((o) => o.code).filter(
    (code) => code !== 'CONFIRMED'
);

function buildDefaultQuoteStatusSelections() {
    return Object.fromEntries(
        CATERING_QUOTE_STATUS_OPTS.map((o) => [o.code, o.code === 'REQUESTED'])
    );
}

function normalizeQuoteStatusNorm(quote) {
    return String(quote?.quoteStatus ?? '')
        .trim()
        .toUpperCase()
        .replace(/[\s_-]+/g, '');
}

function quotePassesStatusFilter(quote, selections) {
    const allSel = CATERING_QUOTE_STATUS_OPTS.every((o) => selections[o.code]);
    if (allSel) return true;
    const anySel = CATERING_QUOTE_STATUS_OPTS.some((o) => selections[o.code]);
    if (!anySel) return true;
    const norm = normalizeQuoteStatusNorm(quote);
    for (const opt of CATERING_QUOTE_STATUS_OPTS) {
        if (opt.matchNorm.includes(norm)) return !!selections[opt.code];
    }
    return false;
}

function quoteMatchesKeyword(quote, keyword) {
    const q = String(keyword ?? '').trim().toLowerCase();
    if (!q) return true;
    const parts = [
        quote.cateringQuoteId,
        quote.customerId,
        quote.customerName,
        quote.customerPhone,
        quote.packageName,
        quote.eventType,
        quote.eventDate,
        quote.deliveryType,
        quote.deliveryAddress,
        quote.quoteStatus,
        quote.estimatedTotal,
        quote.quotedTotal,
        quote.numberOfGuests,
        ...(quote.lineItems ?? []).map((li) => li.itemName),
    ];
    return parts.filter(Boolean).join(' ').toLowerCase().includes(q);
}

function formatDateDisplay(raw) {
    if (!raw) return 'N/A';
    const s = String(raw).trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
        const [y, m, d] = s.slice(0, 10).split('-');
        return `${d}/${m}/${y}`;
    }
    try {
        const date = new Date(s);
        if (!Number.isNaN(date.getTime())) {
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            return `${day}/${month}/${year}`;
        }
    } catch (e) {
        /* ignore */
    }
    return s;
}

function formatMoney(value) {
    if (value == null || value === '') return 'N/A';
    const n = Number.parseFloat(value);
    if (!Number.isFinite(n)) return String(value);
    return n.toFixed(2);
}

function emptyEditLineRow() {
    return {
        localKey: `new-${Date.now()}-${Math.random()}`,
        cateringQuoteLineItemId: null,
        cateringItemId: '',
        itemName: '',
        quantity: '1',
        unitPrice: '',
    };
}

function mapQuoteLinesToEditRows(lineItems) {
    return (lineItems ?? []).map((li, index) => ({
        localKey: `existing-${li.cateringQuoteLineItemId ?? index}`,
        cateringQuoteLineItemId: li.cateringQuoteLineItemId ?? null,
        originalCateringItemId: li.cateringItemId != null ? String(li.cateringItemId) : '',
        cateringItemId: li.cateringItemId != null ? String(li.cateringItemId) : '',
        itemName: li.itemName ?? '',
        quantity: String(li.quantity ?? 1),
        unitPrice:
            li.unitPrice != null && li.unitPrice !== ''
                ? String(li.unitPrice)
                : li.price != null && li.quantity
                  ? String(Number(li.price) / Math.max(Number(li.quantity) || 1, 1))
                  : '',
    }));
}

function formatDeliveryType(value) {
    const raw = String(value ?? '').trim();
    if (!raw) return '—';
    if (raw.toUpperCase() === 'DELIVERY') return 'Delivery';
    if (raw.toUpperCase() === 'PICKUP') return 'Pickup';
    return raw.replace(/_/g, ' ');
}

function quoteSortValue(quote, key) {
    switch (key) {
        case 'orderId':
            return String(quote.cateringQuoteId ?? '');
        case 'customerName':
            return quote.customerName ?? '';
        case 'customerPhone':
            return quote.customerPhone ?? '';
        case 'eventDate':
            return quote.eventDate ?? '';
        case 'eventType':
            return quote.eventType ?? '';
        case 'deliveryType':
            return quote.deliveryType ?? '';
        case 'eventLocation':
            return quote.deliveryAddress ?? '';
        case 'numberOfGuests':
            return Number(quote.numberOfGuests) || 0;
        case 'totalPrice':
            return Number.parseFloat(quote.estimatedTotal) || 0;
        case 'orderStatus':
            return quote.quoteStatus ?? '';
        case 'createdDate':
            return quote.createdDate ?? '';
        default:
            return '';
    }
}

function sortQuoteList(list, sort) {
    if (!sort?.key) return list;
    const dir = sort.direction === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
        const va = quoteSortValue(a, sort.key);
        const vb = quoteSortValue(b, sort.key);
        if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
        return String(va).localeCompare(String(vb), undefined, { numeric: true }) * dir;
    });
}

const CateringQuotesPanel = ({
    restaurantId,
    ordersPageSize,
    onPageSizeChange,
    onCateringSectionChange,
    serviceChannelSelect = null,
}) => {
    const navigate = useNavigate();
    const [quotes, setQuotes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [statusSelections, setStatusSelections] = useState(buildDefaultQuoteStatusSelections);
    const [sort, setSort] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [expandedQuoteId, setExpandedQuoteId] = useState(null);
    const [editingQuote, setEditingQuote] = useState(null);
    const [editQuoteStatus, setEditQuoteStatus] = useState('REQUESTED');
    const [editQuotedTotal, setEditQuotedTotal] = useState('');
    const [editLineRows, setEditLineRows] = useState([]);
    const [editMenuItems, setEditMenuItems] = useState([]);
    const [editMenuLoading, setEditMenuLoading] = useState(false);
    const [editSaving, setEditSaving] = useState(false);
    const [editError, setEditError] = useState('');

    const actorId = useMemo(() => {
        try {
            const user = JSON.parse(localStorage.getItem('user') || 'null');
            return Number(user?.userId ?? user?.User_Id ?? user?.user_id ?? 1);
        } catch (e) {
            return 1;
        }
    }, []);

    const editItemLookup = useMemo(() => {
        const map = new Map();
        editMenuItems.forEach((item) => {
            map.set(String(item.Catering_Item_Id), item);
        });
        return map;
    }, [editMenuItems]);

    const editLinesSubtotal = useMemo(() => {
        return editLineRows.reduce((sum, row) => {
            const item = editItemLookup.get(String(row.cateringItemId));
            const unit =
                Number.parseFloat(row.unitPrice) ||
                Number.parseFloat(item?.Price) ||
                0;
            const qty = Number.parseInt(row.quantity, 10) || 0;
            return sum + unit * qty;
        }, 0);
    }, [editLineRows, editItemLookup]);

    const goToQuoteDetail = (quoteId) => {
        navigate(`/catering-quote/${quoteId}`);
    };

    const { handleSortAsc, handleSortDesc } = buildSortHandlers(setSort, () => setCurrentPage(1));

    const loadQuotes = useCallback(async () => {
        if (!restaurantId) {
            setQuotes([]);
            return;
        }
        setLoading(true);
        try {
            const rows = await fetchCateringQuotesByRestaurant(restaurantId);
            setQuotes(rows);
        } catch (err) {
            console.error('Failed to load catering quotes:', err?.response?.data ?? err.message);
            setQuotes([]);
        } finally {
            setLoading(false);
        }
    }, [restaurantId]);

    useEffect(() => {
        loadQuotes();
    }, [loadQuotes]);

    useEffect(() => {
        setCurrentPage(1);
    }, [search, statusSelections, sort]);

    const filteredQuotes = useMemo(() => {
        let list = quotes.filter((q) => quotePassesStatusFilter(q, statusSelections));
        if (search.trim()) {
            list = list.filter((q) => quoteMatchesKeyword(q, search));
        }
        return sortQuoteList(list, sort);
    }, [quotes, statusSelections, search, sort]);

    const indexOfLast = currentPage * ordersPageSize;
    const indexOfFirst = indexOfLast - ordersPageSize;
    const currentQuotes = filteredQuotes.slice(indexOfFirst, indexOfLast);
    const totalPages = Math.ceil(filteredQuotes.length / ordersPageSize) || 1;

    const toggleStatus = (code) => {
        setStatusSelections((prev) => ({ ...prev, [code]: !prev[code] }));
    };

    const setAllStatuses = (checked) => {
        setStatusSelections((prev) => {
            const next = { ...prev };
            CATERING_QUOTE_STATUS_OPTS.forEach((opt) => {
                next[opt.code] = checked;
            });
            return next;
        });
    };

    const openEditQuote = async (quote) => {
        const initialRows = mapQuoteLinesToEditRows(quote.lineItems);
        const status = quote.quoteStatus || 'REQUESTED';
        setEditingQuote(quote);
        // Confirmed creates an order — keep that as an explicit action, not a status dropdown value.
        setEditQuoteStatus(status === 'CONFIRMED' ? 'UNDER_REVIEW' : status);
        setEditQuotedTotal(quote.quotedTotal != null ? String(quote.quotedTotal) : '');
        setEditLineRows(initialRows.length ? initialRows : [emptyEditLineRow()]);
        setEditError('');
        setEditMenuItems([]);

        const rid = quote.restaurantId ?? restaurantId;
        if (!rid) return;
        setEditMenuLoading(true);
        try {
            const items = await fetchCateringMenuItemsForTemplate(rid);
            setEditMenuItems(items);
        } catch (err) {
            console.error('Failed to load catering menu for quote edit:', err);
            setEditError('Could not load catering menu items.');
        } finally {
            setEditMenuLoading(false);
        }
    };

    const closeEditQuote = () => {
        if (editSaving) return;
        setEditingQuote(null);
        setEditLineRows([]);
        setEditMenuItems([]);
        setEditError('');
    };

    const updateEditLineRow = (localKey, patch) => {
        setEditLineRows((rows) =>
            rows.map((row) => (row.localKey === localKey ? { ...row, ...patch } : row))
        );
    };

    const handleEditLineItemChange = (localKey, cateringItemId) => {
        const item = editItemLookup.get(String(cateringItemId));
        updateEditLineRow(localKey, {
            cateringItemId: cateringItemId ? String(cateringItemId) : '',
            itemName: item?.Food_Item ?? '',
            unitPrice: item?.Price != null ? String(item.Price) : '',
        });
    };

    const removeEditLineRow = (localKey) => {
        setEditLineRows((rows) => {
            if (rows.length <= 1) return rows;
            return rows.filter((row) => row.localKey !== localKey);
        });
    };

    const buildQuoteUpdatePayload = ({ confirmOrder = false } = {}) => {
        const lineItems = [];
        for (const row of editLineRows) {
            if (!row.cateringItemId) continue;
            const qty = Number.parseInt(row.quantity, 10);
            if (!Number.isFinite(qty) || qty <= 0) {
                return { error: 'Each line item needs a quantity greater than 0.' };
            }
            const item = editItemLookup.get(String(row.cateringItemId));
            const unitPrice =
                Number.parseFloat(row.unitPrice) ||
                Number.parseFloat(item?.Price) ||
                0;
            const payloadLine = {
                cateringItemId: Number(row.cateringItemId),
                itemName: row.itemName || item?.Food_Item || undefined,
                foodCategoryId: item?.foodCategoryId,
                quantity: qty,
                unitPrice,
                price: unitPrice * qty,
            };
            if (
                row.cateringQuoteLineItemId != null &&
                String(row.cateringItemId) === String(row.originalCateringItemId ?? '')
            ) {
                payloadLine.cateringQuoteLineItemId = Number(row.cateringQuoteLineItemId);
            }
            lineItems.push(payloadLine);
        }

        if (lineItems.length === 0) {
            return { error: 'Add at least one catering menu item.' };
        }

        const payload = {
            updatedBy: actorId,
            lineItems,
        };

        if (confirmOrder) {
            payload.quoteStatus = 'CONFIRMED';
        } else {
            const nextStatus = editQuoteStatus || 'REQUESTED';
            if (nextStatus === 'CONFIRMED') {
                return {
                    error: 'Use “Confirm & create order” to confirm this quote. Saving only updates the quote.',
                };
            }
            payload.quoteStatus = nextStatus;
        }

        if (editQuotedTotal.trim() !== '') {
            const quoted = Number.parseFloat(editQuotedTotal);
            if (!Number.isFinite(quoted) || quoted < 0) {
                return { error: 'Enter a valid quoted total.' };
            }
            payload.quotedTotal = quoted;
        }

        return { payload };
    };

    const applyQuoteUpdateResult = (updated) => {
        const normalized = normalizeCateringQuote(updated) ?? updated;
        setQuotes((prev) =>
            prev.map((q) =>
                q.cateringQuoteId === normalized.cateringQuoteId ? { ...q, ...normalized } : q
            )
        );
        setEditingQuote(null);
        setEditLineRows([]);
        setEditMenuItems([]);
        setEditError('');
    };

    const handleSaveQuoteEdit = async () => {
        if (!editingQuote?.cateringQuoteId || editSaving) return;
        const built = buildQuoteUpdatePayload({ confirmOrder: false });
        if (built.error) {
            setEditError(built.error);
            return;
        }

        setEditSaving(true);
        setEditError('');
        try {
            const updated = await updateCateringQuote(editingQuote.cateringQuoteId, built.payload);
            applyQuoteUpdateResult(updated);
        } catch (err) {
            setEditError(
                err?.response?.data?.error ||
                    err?.response?.data?.message ||
                    err.message ||
                    'Failed to update quote'
            );
        } finally {
            setEditSaving(false);
        }
    };

    const handleConfirmQuoteAndCreateOrder = async () => {
        if (!editingQuote?.cateringQuoteId || editSaving) return;
        const built = buildQuoteUpdatePayload({ confirmOrder: true });
        if (built.error) {
            setEditError(built.error);
            return;
        }

        const confirmed = await confirmAction({
            title: 'Confirm this quote?',
            message:
                'Confirming will create a catering order from this quote. This cannot be undone from here.',
            confirmLabel: 'Confirm & create order',
            cancelLabel: 'Keep editing',
        });
        if (!confirmed) return;

        setEditSaving(true);
        setEditError('');
        try {
            const updated = await updateCateringQuote(editingQuote.cateringQuoteId, built.payload);
            applyQuoteUpdateResult(updated);
        } catch (err) {
            setEditError(
                err?.response?.data?.error ||
                    err?.response?.data?.message ||
                    err.message ||
                    'Failed to confirm quote'
            );
        } finally {
            setEditSaving(false);
        }
    };

    return (
        <>
            <div className="today-order-toolbar today-order-toolbar--quote-actions">
                <div className="today-order-toolbar__row">
                    <div className="today-order-toolbar__main">
                        {serviceChannelSelect}
                        {typeof onCateringSectionChange === 'function' ? (
                            <CateringViewSwitch
                                activeSection="quotes"
                                onChange={onCateringSectionChange}
                            />
                        ) : null}
                        <CollapsibleFilters>
                        <OrderStatusFilterDropdown
                            panelId="catering-quote-status-filter"
                            statusOptions={CATERING_QUOTE_STATUS_OPTS}
                            statusSelections={statusSelections}
                            onToggleStatus={toggleStatus}
                            onSetAllStatuses={setAllStatuses}
                        />
                        </CollapsibleFilters>
                        <button
                            type="button"
                            className="menu-toolbar-btn today-order-toolbar__refresh"
                            onClick={loadQuotes}
                            aria-label="Refresh quotes"
                            title="Refresh"
                            disabled={loading}
                        >
                            <FaSyncAlt aria-hidden="true" />
                        </button>
                    </div>
                    <button
                        type="button"
                        className="add-button today-order-toolbar__action"
                        onClick={() => navigate('/catering-quote/create')}
                    > Add quote
                    </button>
                </div>
            </div>

            <OrdersTablePanel
                searchValue={search}
                onSearchChange={setSearch}
                resultCount={filteredQuotes.length}
                tableWrapperClassName="today-order-table-container"
            >
                <table className="menu-table today-order-table today-order-table--catering">
                    <thead>
                        <tr>
                            <OrdersSortableTh label="Quote#" sortKey="orderId" activeSortKey={sort?.key} sortDirection={sort?.direction} onSortAsc={handleSortAsc} onSortDesc={handleSortDesc} />
                            <OrdersSortableTh label="Customer Name" sortKey="customerName" activeSortKey={sort?.key} sortDirection={sort?.direction} onSortAsc={handleSortAsc} onSortDesc={handleSortDesc} />
                            <OrdersSortableTh label="Phone" sortKey="customerPhone" activeSortKey={sort?.key} sortDirection={sort?.direction} onSortAsc={handleSortAsc} onSortDesc={handleSortDesc} />
                            <OrdersSortableTh label="Event Date" sortKey="eventDate" activeSortKey={sort?.key} sortDirection={sort?.direction} onSortAsc={handleSortAsc} onSortDesc={handleSortDesc} />
                            <OrdersSortableTh label="Event Type" sortKey="eventType" activeSortKey={sort?.key} sortDirection={sort?.direction} onSortAsc={handleSortAsc} onSortDesc={handleSortDesc} />
                            <OrdersSortableTh label="Delivery Type" sortKey="deliveryType" activeSortKey={sort?.key} sortDirection={sort?.direction} onSortAsc={handleSortAsc} onSortDesc={handleSortDesc} />
                            <OrdersSortableTh label="Location" sortKey="eventLocation" activeSortKey={sort?.key} sortDirection={sort?.direction} onSortAsc={handleSortAsc} onSortDesc={handleSortDesc} />
                            <th>Food Items</th>
                            <OrdersSortableTh label="Guests" sortKey="numberOfGuests" activeSortKey={sort?.key} sortDirection={sort?.direction} onSortAsc={handleSortAsc} onSortDesc={handleSortDesc} />
                            <th>Package</th>
                            <OrdersSortableTh label="Estimated ₹" sortKey="totalPrice" activeSortKey={sort?.key} sortDirection={sort?.direction} onSortAsc={handleSortAsc} onSortDesc={handleSortDesc} />
                            <th>Quoted ₹</th>
                            <OrdersSortableTh label="Status" sortKey="orderStatus" activeSortKey={sort?.key} sortDirection={sort?.direction} onSortAsc={handleSortAsc} onSortDesc={handleSortDesc} />
                            <OrdersSortableTh label="Created On" sortKey="createdDate" activeSortKey={sort?.key} sortDirection={sort?.direction} onSortAsc={handleSortAsc} onSortDesc={handleSortDesc} />
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={15} className="no-orders-message menu-table-empty-cell">
                                    Loading quote requests…
                                </td>
                            </tr>
                        ) : currentQuotes.length > 0 ? (
                            currentQuotes.map((quote) => {
                                const lineItems = (quote.lineItems ?? []).map((li) => ({
                                    itemName: li.itemName,
                                    quantity: li.quantity,
                                    price: li.price,
                                }));
                                const quoteClosed = isTerminalQuoteStatus(quote.quoteStatus);
                                return (
                                    <React.Fragment key={quote.cateringQuoteId}>
                                        <tr>
                                            <td>
                                                <span
                                                    className="order-id-link"
                                                    onClick={() => goToQuoteDetail(quote.cateringQuoteId)}
                                                >
                                                    {quote.cateringQuoteId}
                                                </span>
                                            </td>
                                            <td>{quote.customerName || '—'}</td>
                                            <td>{formatMobileDisplayOrFallback(quote.customerPhone, '—')}</td>
                                            <td>{formatDateDisplay(quote.eventDate)}</td>
                                            <td>{quote.eventType || 'N/A'}</td>
                                            <td>{formatDeliveryType(quote.deliveryType)}</td>
                                            <td>{quote.deliveryAddress || '—'}</td>
                                            <td>
                                                <OrderItemsSummary
                                                    items={lineItems}
                                                    onClick={() =>
                                                        setExpandedQuoteId(
                                                            expandedQuoteId === quote.cateringQuoteId
                                                                ? null
                                                                : quote.cateringQuoteId
                                                        )
                                                    }
                                                    emptyText="No items"
                                                />
                                            </td>
                                            <td>{quote.numberOfGuests ?? 'N/A'}</td>
                                            <td>{quote.packageName || 'N/A'}</td>
                                            <td>{formatMoney(quote.estimatedTotal)}</td>
                                            <td>{formatMoney(quote.quotedTotal)}</td>
                                            <td>
                                                <OrderStatusPill status={quote.quoteStatus ?? 'REQUESTED'} />
                                            </td>
                                            <td>{formatDateDisplay(quote.createdDate)}</td>
                                            <td className="action-cell menu-table-actions-cell">
                                                {!quoteClosed && (
                                                    <button
                                                        type="button"
                                                        className="action-button"
                                                        title="Edit quote status"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            openEditQuote(quote);
                                                        }}
                                                    >
                                                        <FaEdit />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                        {expandedQuoteId === quote.cateringQuoteId && lineItems.length > 0 && (
                                            <tr>
                                                <td colSpan={15}>
                                                    <ul className="today-order-food-list">
                                                        {lineItems.map((item, index) => (
                                                            <li key={index}>
                                                                {item.itemName} — Qty: {item.quantity}, ₹{formatMoney(item.price)}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan={15} className="no-orders-message menu-table-empty-cell">
                                    No quote requests
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </OrdersTablePanel>

            <OrdersTablePagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={filteredQuotes.length}
                pageSize={ordersPageSize}
                onPageChange={setCurrentPage}
                onPageSizeChange={onPageSizeChange}
            />

            {editingQuote ? (
                <div className="popup-overlay">
                    <div className="popup-box popup-box--update-order popup-box--quote-edit">
                        <div className="assign-agents-modal-header">
                            <h2>Update quote #{editingQuote.cateringQuoteId}</h2>
                            <button
                                type="button"
                                className="menu-template-modal-close"
                                aria-label="Close"
                                onClick={closeEditQuote}
                                disabled={editSaving}
                            >
                                <FaTimes />
                            </button>
                        </div>
                        <div className="today-order-update-form">
                            <div className="today-order-modal-status-column">
                                <label htmlFor="quote-edit-status" className="today-order-modal-field-label">
                                    Quote status
                                </label>
                                <select
                                    id="quote-edit-status"
                                    className="today-order-select"
                                    value={editQuoteStatus}
                                    onChange={(e) => setEditQuoteStatus(e.target.value)}
                                    style={{ width: '100%' }}
                                    disabled={editSaving}
                                >
                                    {QUOTE_STATUS_EDIT_OPTIONS.map((status) => (
                                        <option key={status} value={status}>
                                            {status.replace(/_/g, ' ')}
                                        </option>
                                    ))}
                                </select>
                                <p className="catering-quote-create-muted" style={{ margin: '6px 0 0' }}>
                                    Saving updates the quote only. Use Confirm to create a catering order.
                                </p>
                            </div>
                            <div className="today-order-modal-status-column" style={{ marginTop: '12px' }}>
                                <label htmlFor="quote-edit-total" className="today-order-modal-field-label">
                                    Quoted total (₹)
                                </label>
                                <input
                                    id="quote-edit-total"
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    className="today-order-select"
                                    value={editQuotedTotal}
                                    onChange={(e) => setEditQuotedTotal(e.target.value)}
                                    placeholder={
                                        editLinesSubtotal > 0
                                            ? editLinesSubtotal.toFixed(2)
                                            : editingQuote.estimatedTotal != null
                                              ? String(editingQuote.estimatedTotal)
                                              : ''
                                    }
                                    style={{ width: '100%', boxSizing: 'border-box' }}
                                    disabled={editSaving}
                                />
                                <p className="catering-quote-create-muted" style={{ margin: '6px 0 0' }}>
                                    Items subtotal: ₹{editLinesSubtotal.toFixed(2)}
                                    {editQuotedTotal.trim() === ''
                                        ? ' (quoted total will use this if left blank)'
                                        : ''}
                                </p>
                            </div>

                            <div className="quote-edit-items" style={{ marginTop: '16px' }}>
                                <div className="catering-quote-create-section-head">
                                    <label className="today-order-modal-field-label" style={{ margin: 0 }}>
                                        Food items
                                    </label>
                                    <button
                                        type="button"
                                        className="menu-toolbar-btn"
                                        onClick={() =>
                                            setEditLineRows((rows) => [...rows, emptyEditLineRow()])
                                        }
                                        disabled={editSaving || editMenuLoading}
                                    >
                                        <FaPlus aria-hidden /> Add item
                                    </button>
                                </div>
                                {editMenuLoading ? (
                                    <p className="catering-quote-create-muted">Loading catering menu…</p>
                                ) : (
                                    <div className="catering-quote-create-lines-wrap">
                                        <table className="order-details-rich-table catering-quote-create-lines">
                                            <thead>
                                                <tr>
                                                    <th>Item</th>
                                                    <th>Qty</th>
                                                    <th>Unit ₹</th>
                                                    <th>Line ₹</th>
                                                    <th>Remove</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {editLineRows.map((row) => {
                                                    const item = editItemLookup.get(
                                                        String(row.cateringItemId)
                                                    );
                                                    const unit =
                                                        Number.parseFloat(row.unitPrice) ||
                                                        Number.parseFloat(item?.Price) ||
                                                        0;
                                                    const qty = Number.parseInt(row.quantity, 10) || 0;
                                                    return (
                                                        <tr key={row.localKey}>
                                                            <td>
                                                                <select
                                                                    value={row.cateringItemId}
                                                                    onChange={(e) =>
                                                                        handleEditLineItemChange(
                                                                            row.localKey,
                                                                            e.target.value
                                                                        )
                                                                    }
                                                                    disabled={editSaving}
                                                                >
                                                                    <option value="">Select item</option>
                                                                    {editMenuItems.map((menuItem) => (
                                                                        <option
                                                                            key={menuItem.Catering_Item_Id}
                                                                            value={menuItem.Catering_Item_Id}
                                                                        >
                                                                            {menuItem.Food_Item}
                                                                        </option>
                                                                    ))}
                                                                    {row.cateringItemId &&
                                                                    !editItemLookup.has(
                                                                        String(row.cateringItemId)
                                                                    ) ? (
                                                                        <option value={row.cateringItemId}>
                                                                            {row.itemName ||
                                                                                `Item #${row.cateringItemId}`}
                                                                        </option>
                                                                    ) : null}
                                                                </select>
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="number"
                                                                    min="1"
                                                                    step="1"
                                                                    value={row.quantity}
                                                                    onChange={(e) =>
                                                                        updateEditLineRow(row.localKey, {
                                                                            quantity: e.target.value,
                                                                        })
                                                                    }
                                                                    disabled={editSaving}
                                                                />
                                                            </td>
                                                            <td>{formatMoney(unit)}</td>
                                                            <td>{formatMoney(unit * qty)}</td>
                                                            <td>
                                                                <button
                                                                    type="button"
                                                                    className="action-button delete-button"
                                                                    title="Remove item"
                                                                    aria-label="Remove item"
                                                                    onClick={() =>
                                                                        removeEditLineRow(row.localKey)
                                                                    }
                                                                    disabled={
                                                                        editSaving ||
                                                                        editLineRows.length <= 1
                                                                    }
                                                                >
                                                                    <FaTrash aria-hidden />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>

                            {editError ? (
                                <p style={{ color: '#b91c1c', marginTop: '12px', fontSize: '14px' }}>
                                    {editError}
                                </p>
                            ) : null}
                            <div className="today-order-update-form__actions today-order-update-form__actions--quote-edit">
                                <button
                                    type="button"
                                    className="add-button"
                                    onClick={handleSaveQuoteEdit}
                                    disabled={editSaving || editMenuLoading}
                                >
                                    {editSaving ? 'Saving…' : 'Save quote'}
                                </button>
                                <button
                                    type="button"
                                    className="add-button"
                                    onClick={handleConfirmQuoteAndCreateOrder}
                                    disabled={editSaving || editMenuLoading}
                                >
                                    Confirm & create order
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}

        </>
    );
};

export default CateringQuotesPanel;
